<template>
  <span>
    <span class='x-filter-condition' :class="'x-filter-condition-' + getConditionType()">
      {{ getConditionSummary() }}
      <i
        class="x-filter-condition-remove icon yule-icon-close-small"
        @click="removeCondition()"
      />
    </span>
    <span v-if="isLast == false" class='x-filter-operator' :class="'x-filter-operator-' + operator"/>
  </span>
</template>

<script>
import moment from "moment"
import Countries from '@/config/data/countries'

export default {
  props: {
    condition: {
      type: Object,
      required: true,
    },
    operator: {
      type: String,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
    }
  },
  beforeMount() {
  },
  mounted() {
  },
  methods: {
    getConditionType() {
      return _.get(this.condition, 'definition.type', 'text')
    },
    getConditionAttribute() {
      return _.get(this.condition, 'definition.attribute', 'text')
    },
    getConditionLabel() {
      return _.get(this.condition, 'definition.condition_label', _.get(this.condition, 'definition.label', '??'))
    },
    getConditionValue() {
      return _.get(this.condition, 'value', null)
    },
    getConditionPredicate() {
      return _.get(this.condition, 'predicate', 'eq')
    },
    getConditionValuesFormatted() {

      let values = this.getConditionValue()
      if (values === null) {
        return []
      }

      if (Array.isArray(values) == false) {
        values = [ values ]
      }

      let type = this.getConditionType()
      let predicate = this.getConditionPredicate()

      let labels = values
      if (type == 'set' || (type == 'date' && predicate == 'in_range')) {
        let options = this.getConditionOptions()
        labels = values.map((value) => {
          let match = options.find((option) => {
            return option.value == value
          })
          if (match == undefined) {
            return '??'
          }
          return type == 'date' ? match.name.toLowerCase() : match.name
        })
      }

      if (type == 'date' && predicate != 'in_range') {
        labels = values.map((value, idx) => {
          // First convert each date to a moment object
          // For the second (to) date, we subtract one day, because we store it as +1 day
          // (see XFilterGroup.addCondition())
          return value ? (idx == 0 ? moment(value) : moment(value).subtract(1, 'day')) : null
        }).filter((date) => {
          // Then filter out any that are null
          return date != null
        }).map((date) => {
          // Finally format the remainder
          // In the interest of compactness, we only show the year if it is not this year
          let label = date.format('D MMM')
          if (date.year() != moment().year()) {
            label += ', ' + date.format('YYYY')
          }
          return label
        })
      }

      if (labels.length == 1) {
        return labels
      } else if (type == 'date') {
        if (labels[0] == labels[1]) {
          return [ labels[0] ]
        } else {
          return labels
        }
      } else {
        let last = labels.pop()
        return [ labels.join(', ') + ' ' + this.$t('ui.xfilters.conditions.operators.or') + ' ' + last ]
      }
    },
    getConditionOptions() {

      let options = _.get(this.condition, 'definition.options', [])

      // If no options have been defined and the attribute is of type country,
      // then we load in the default list of countries.
      if (options == false && this.getConditionAttribute() == 'country') {
        options = []
        let language = this.$store.getters.getCurrentLanguage || 'en'
        Countries[language].forEach((country) => {
          options.push({
            name: country.name,
            value: country.value
          })
        })
      }

      // If no options have been defined and the attribute is of type tags,
      // then we use the values themselves as the option names
      if (options == false && this.getConditionAttribute() == 'tags') {
        let values = this.getConditionValue()
        if (values === null) {
          values = []
        }
        if (Array.isArray(values) == false) {
          values = [ values ]
        }
        values.forEach((value) => {
          options.push({
            name: value,
            value: value
          })
        })
      }

      if (options == false) {
        options = []
      }

      return options
    },
    getConditionSummaryTemplate() {

      let predicate = this.getConditionPredicate()
      let type = this.getConditionType()
      let value = this.getConditionValue()

      if (type == 'date') {
        if (predicate == 'in_range') {
          return /^past_/.test(value)
            ? 'ui.xfilters.conditions.summary.date_in_range'
            : 'ui.xfilters.conditions.summary.date_in_range_simple'
        }
        if (Array.isArray(value) && value[0] && value[1]) {
          if (moment(value[0]).format('YYYY-MM-DD') == moment(value[1]).subtract(1, 'day').format('YYYY-MM-DD')) {
            return 'ui.xfilters.conditions.summary.date_on'
          } else {
            return 'ui.xfilters.conditions.summary.date_between'
          }
        }
        if (Array.isArray(value) && !value[1]) {
          return 'ui.xfilters.conditions.summary.date_since'
        }
        if (Array.isArray(value) && !value[0]) {
          return 'ui.xfilters.conditions.summary.date_before'
        }
      }

      if (predicate == 'eq') {
        return 'ui.xfilters.conditions.summary.is'
      }

      if (['has_any', 'has_all'].includes(predicate)) {
        if (Array.isArray(value) && value.length == 1) {
          return 'ui.xfilters.conditions.summary.includes'
        }
        else if (predicate == 'has_any') {
          return 'ui.xfilters.conditions.summary.has_any'
        } else if (predicate == 'has_all') {
          return 'ui.xfilters.conditions.summary.has_all'
        }
      }

      if (predicate == 'has_none') {
        return 'ui.xfilters.conditions.summary.is_not_assigned'
      }

      return 'ui.xfilters.conditions.summary.is'
    },
    getConditionSummary() {
      // Get the i18n key used for this conidition and values
      let template = this.getConditionSummaryTemplate()

      // Get an array of all values, formatted as necessary
      let formattedValues = this.getConditionValuesFormatted()

      // Add the condition label to front of the final array
      let label = this.getConditionLabel()
      let templateValues = formattedValues
      templateValues.unshift(label)

      // Resolve i18n key with all values inserted
      let summary = this.$t(template, templateValues)

      // Format it by uppercasing only the first character
      summary = summary.toLowerCase()
      summary = summary.charAt(0).toUpperCase() + summary.slice(1)

      return summary
    },
    removeCondition() {
      // Delegate to the parent group
      this.$emit('removeCondition', this.condition.id)
    },
  },
}
</script>

<style lang="sass" scoped>
</style>


// WEBPACK FOOTER //
// XFilterCondition.vue?7d60eca8